// Admin routes for the redesigned Settings pages (owner, 2026-10-22: "work on settings like you do on
// online store"). The router mounts whatever this returns under the app shell, before the placeholders.
// Light merge: Order protection and Partial payment are tabs of Checkout, Markets a tab of Language and
// region, Business verification and the Activity log tabs of Organization. Their old addresses open the
// tab that replaced them; the old Store email address opens Notifications → Customer messages (D-112).
import { useEffect } from "react";
import {
  createRoute,
  useNavigate,
  useParams,
  type AnyRoute,
  type RouteComponent,
  lazyRouteComponent,
} from "@tanstack/react-router";

// Pages are code-split: each loads when its route opens, not with the shell.
const load1 = () => import("../settings/CheckoutPage");
const CheckoutSettingsPage = lazyRouteComponent(load1, "CheckoutSettingsPage");
const load2 = () => import("../settings/DigitalProductsPage");
const DigitalProductsPage = lazyRouteComponent(load2, "DigitalProductsPage");
const load3 = () => import("../settings/HelpPage");
const HelpPage = lazyRouteComponent(load3, "HelpPage");
const load4 = () => import("../settings/LoyaltyPage");
const LoyaltyPage = lazyRouteComponent(load4, "LoyaltyPage");
const load5 = () => import("../settings/OrganizationPage");
const OrganizationPage = lazyRouteComponent(load5, "OrganizationPage");
const load6 = () => import("../settings/RegionPage");
const RegionPage = lazyRouteComponent(load6, "RegionPage");
const load7 = () => import("../settings/StoreProfilePage");
const StoreProfilePage = lazyRouteComponent(load7, "StoreProfilePage");

const STORE_PAGES = [
  ["settings/store", StoreProfilePage],
  ["settings/checkout", CheckoutSettingsPage],
  ["settings/loyalty", LoyaltyPage],
  ["settings/digital-products", DigitalProductsPage],
  ["settings/region", RegionPage],
  ["settings/help", HelpPage],
] as const;

/** Old store-scoped address → [new address, tab]. */
const STORE_MOVED = [
  ["settings/checkout/protection", "settings/checkout", "protection"],
  ["settings/partial-payment", "settings/checkout", "deposits"],
  ["settings/markets", "settings/region", "markets"],
  ["settings/email", "settings/notifications", "messages"],
] as const;

/** Old org-scoped address → [new address, tab]. */
const ORG_MOVED = [
  ["settings/kyc", "settings/organization", "verification"],
  ["settings/activity", "settings/organization", "activity"],
] as const;

function moved(to: string, tab: string, scope: "store" | "org") {
  return function Moved() {
    const { org, store } = useParams({ strict: false }) as { org: string; store?: string };
    const navigate = useNavigate();
    useEffect(() => {
      const base = scope === "org" ? `/${org}` : `/${org}/${store}`;
      void navigate({ to: `${base}/${to}`, search: { tab } as never, replace: true });
    }, [navigate, org, store]);
    return null;
  };
}

export function settingsRoutes(appRoute: AnyRoute): AnyRoute[] {
  const at = (path: string, component: RouteComponent) =>
    createRoute({ getParentRoute: () => appRoute, path, component }) as unknown as AnyRoute;
  return [
    ...STORE_PAGES.map(([path, C]) => at(`/$org/$store/${path}`, () => <C />)),
    ...STORE_MOVED.map(([from, to, tab]) => at(`/$org/$store/${from}`, moved(to, tab, "store"))),
    at("/$org/settings/organization", () => <OrganizationPage />),
    ...ORG_MOVED.map(([from, to, tab]) => at(`/$org/${from}`, moved(to, tab, "org"))),
  ];
}
